import {RefObject, useEffect, useRef, useState} from 'react'
import './floating-cards-background.css'

interface Props { scrollerRef: RefObject<HTMLDivElement | null> }
const MEDIA = '/media/cards-background'

// Independent decorative layer: existing hero content and CardPreview stay intact.
export const FloatingCardsBackground = ({scrollerRef}: Props) => {
    const layerRef = useRef<HTMLDivElement>(null)
    const videoRef = useRef<HTMLVideoElement>(null)
    const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 600px)').matches)

    useEffect(() => {
        const query = window.matchMedia('(max-width: 600px)')
        const update = () => setMobile(query.matches)
        query.addEventListener('change', update)
        return () => query.removeEventListener('change', update)
    }, [])

    useEffect(() => {
        const scroller = scrollerRef.current
        const video = videoRef.current
        const layer = layerRef.current
        if (!scroller || !video || !layer) return
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
        let raf = 0
        let disposed = false
        const update = () => {
            raf = 0
            const progress = Math.min(1, Math.max(0, scroller.scrollTop / Math.max(scroller.clientHeight * 0.85, 1)))
            const opacity = 1 - progress * progress * (3 - 2 * progress)
            layer.style.opacity = String(opacity)
            if (opacity < 0.01 || document.hidden || reduced.matches) {
                video.pause()
            } else if (video.paused) {
                void video.play().then(() => {
                    if (disposed || document.hidden || reduced.matches || Number(layer.style.opacity) < 0.01) video.pause()
                }).catch(() => { /* Autoplay may be unavailable; the poster remains visible. */ })
            }
        }
        const schedule = () => { if (!raf) raf = requestAnimationFrame(update) }
        scroller.addEventListener('scroll', schedule, {passive: true})
        window.addEventListener('resize', schedule)
        document.addEventListener('visibilitychange', schedule)
        reduced.addEventListener('change', schedule)
        video.addEventListener('loadeddata', schedule)
        video.load()
        update()
        return () => {
            disposed = true
            cancelAnimationFrame(raf)
            video.pause()
            scroller.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
            document.removeEventListener('visibilitychange', schedule)
            reduced.removeEventListener('change', schedule)
            video.removeEventListener('loadeddata', schedule)
        }
    }, [mobile, scrollerRef])

    const variant = mobile ? '-mobile' : ''
    return (
        <div ref={layerRef} className="floating-cards-background" aria-hidden="true">
            <video ref={videoRef} muted loop playsInline preload="metadata" tabIndex={-1}
                src={`${MEDIA}${variant}.mp4`} poster={`${MEDIA}${variant}.jpg`} />
            <div className="floating-cards-background-shade" />
        </div>
    )
}
